// Cost per game for Riddled-In (charged server-side, mirrored here for UI display).
export const RIDDLED_IN_COST = 20;

/**
 * Calculate the letter-match percentage between a secret word and a guess.
 *
 * For each unique letter in the secret word, we take the minimum of its
 * frequency in the secret word and in the guess. The sum of those minimums,
 * divided by the total length of the secret word, gives the percentage.
 *
 * This ensures only an exact letter-composition match yields 100%.
 *
 * Example: secret = "COUCH" (C×2, O×1, U×1, H×1)
 *  - guess "CUT"   → C×1 + O×0 + U×1 + H×0 = 2/5 = 40%
 *  - guess "COUGH" → C×1 + O×1 + U×1 + H×1 = 4/5 = 80%
 *  - guess "COUCH" → C×2 + O×1 + U×1 + H×1 = 5/5 = 100%
 */
export const calculateMatchPercentage = (secretWord, guess) => {
  const secret = secretWord.toLowerCase();
  const g = guess.toLowerCase();

  // Count letter frequencies in the secret word
  const secretFreq = {};
  for (const ch of secret) {
    secretFreq[ch] = (secretFreq[ch] || 0) + 1;
  }

  // Count letter frequencies in the guess
  const guessFreq = {};
  for (const ch of g) {
    guessFreq[ch] = (guessFreq[ch] || 0) + 1;
  }

  // Sum of min-frequencies for each letter present in the secret word
  let matched = 0;
  for (const [letter, count] of Object.entries(secretFreq)) {
    matched += Math.min(count, guessFreq[letter] || 0);
  }

  return Math.round((matched / secret.length) * 100);
};

/**
 * Build a per-letter mask for a guess.
 * `true` means that letter exists in the secret word (respecting duplicate counts).
 *
 * Example: secret = "COUCH", guess = "COCOA" -> [true, true, true, false, false]
 */
export const calculateLettersInWordMask = (secretWord, guess) => {
  const secret = secretWord.toLowerCase();
  const g = guess.toLowerCase();

  const remaining = {};
  for (const ch of secret) {
    remaining[ch] = (remaining[ch] || 0) + 1;
  }

  const mask = Array.from(g, () => false);
  for (let i = 0; i < g.length; i += 1) {
    const ch = g[i];
    if ((remaining[ch] || 0) > 0) {
      mask[i] = true;
      remaining[ch] -= 1;
    }
  }

  return mask;
};

/**
 * Returns true only when the guess fills all expected cells.
 */
export const isRiddledInGuessComplete = (guess, wordLength) => {
  const normalizedGuess = String(guess || '').trim();
  const normalizedWordLength = Number(wordLength);
  if (!Number.isFinite(normalizedWordLength) || normalizedWordLength <= 0) return false;
  return normalizedGuess.length === normalizedWordLength;
};
